import { renderedPortalCache } from '@mfec/customerAttributes/renderedPortalCache';
import {
  CustomerAttribute, CustomerAttributeGroup,
} from '@mfec/customerAttributes/types';
import { GetTemplateFn } from '@mfec/lit/mixins/TemplatingMixin';
import { portal, TargetOrSelector } from '@mfec/lit/portal';
import { TefComs } from '@mfec/types/tefComs';
import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';

export type CustomerAttributeTargetType = ShadowRoot | LitElement | HTMLElement;

//
// Base TefComsCustomerAttribute class to extend from in the microfronends
// All throwing methods MUST be overwritten with a compatible version of the MFE
//
export class TefComsCustomerAttributeBase extends LitElement {
  @property({ type: String }) id: string = '';

  // -------------- START: MUST BE OVERWRITTEN ---------------
  // These methods depend on the implementation of the store (redux? mobx? fancynew?)
  //
  get customerAttributes() : CustomerAttribute[] {
    throw new Error('[TefComsCustomerAttributeBase] You must implement get customerAttribute()');
  }

  get userIsInRegisterFlow() : boolean {
    throw new Error('[TefComsCustomerAttributeBase] You must implement get userIsInRegisterFlow()');
  }

  get userIsInEditMode(): boolean {
    throw new Error('[TefComsCustomerAttributeBase] You must implement get userIsInEditMode()');
  }

  get theme() : string {
    throw new Error('[TefComsCustomerAttributeBase] You must implement get theme()');
  }

  saveCustomerAttributeToStore(customerAttribute: CustomerAttribute) : void {
    throw new Error(`[TefComsCustomerAttributeBase] You must implement get saveCustomerAttributeToStore(${String(customerAttribute)})`);
  }

  // The main component tag (eg. "tef-coms")
  get mainComponentTag() : string {
    throw new Error('[TefComsCustomerAttributeBase] You must implement get mainComponentTag()');
  }

  // This can only be found out AFTER initializing the TemplatingMixin in the extending component
  getTemplateFnFor(templatename: string) : GetTemplateFn {
    throw new Error(`[TefComsCustomerAttributeBase] You must implement getTemplateFnFor(${templatename})`);
  }
  // -------------- END: MUST BE OVERWRITTEN ---------------

  connectedCallback(this: TefComsCustomerAttributeBase) {
    super.connectedCallback();
    this.mainComponentNode.addEventListener(this.mainComponentTag, this.handleTefComsEvents.bind(this) as EventListenerOrEventListenerObject);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // web-test-runner sometimes removes the comp before running disconnectedCallbacks :(
    if (this.mainComponentNode) {
      this.mainComponentNode.removeEventListener(this.mainComponentTag, this.handleTefComsEvents.bind(this) as EventListenerOrEventListenerObject);
    }
    this.resetRenderedPortalCache();
  }

  handleTefComsEvents(this: TefComsCustomerAttributeBase, event : TefComs.Event) {
    if (this.renderedPortalsCacheEvents.includes(event.detail.name)) {
      this.resetRenderedPortalCache();
    }
  }

  resetRenderedPortalCache() {
    renderedPortalCache.delete(this.id);
  }

  // Render to lightDOM
  createRenderRoot() {
    return this;
  }

  get customerAttribute() {
    const customerAttribute: CustomerAttribute = this.customerAttributes.find((cattr) => cattr.id === this.id)!;

    // When in view mode, items with undefined "granted" should not be shown
    if (customerAttribute && !this.userIsInRegisterFlow && !this.userIsInEditMode) {
      /* eslint-disable no-param-reassign */
      ['standard', 'extra'].forEach((itemGroupName) => {
        const itemGroup = (customerAttribute[itemGroupName] as CustomerAttributeGroup);
        itemGroup.items.forEach((item) => {
          item.display = true;
          if (typeof item.granted === 'undefined') {
            item.display = false;
          }
        });
      });
      /* eslint-enable no-param-reassign */
    }

    return customerAttribute;
  }

  render() {
    const renderBlocks : unknown[] = [];

    // Inject the template tag which contains the <a>TEXT</a> in the link DOM node
    const template$ = this.renderRoot.querySelector('template');

    const modalTriggerFn = this.getTemplateFnFor('tefComsCustomerAttributeModalTrigger');
    renderBlocks.push(modalTriggerFn(template$));

    // This can happen if there is a [Customer Attribute|cattr|somecattr] in the live-config but our API
    // doesn't send that cattr id back in 'customerAttributes'
    // We fall back to rendering the main text with a defunct modal
    if (!this.customerAttribute) {
      console.warn("Got cattr '%s' in scope without cattr present in config data", this.id); // eslint-disable-line
      return renderBlocks;
    }

    const cachingKey = this.id;
    if (!renderedPortalCache.has(cachingKey)) {
      renderedPortalCache.add(cachingKey);
      const modalDialogFn = this.getTemplateFnFor('tefComsCustomerAttributeModalDialog');
      const modalDialogContent = modalDialogFn(this.customerAttribute, this.itemsDisplayStyle, template$);
      renderBlocks.push(portal(modalDialogContent, this.mainComponentNode.shadowRoot as TargetOrSelector));
    }

    return renderBlocks;
  }

  get customerAttributeItems$() {
    const items$ : NodeListOf<HTMLInputElement> = this.mainComponentNode.shadowRoot!.querySelectorAll(`[data-cattr-id='${this.id}'][data-cattr-item-id]`);
    return items$;
  }

  // Set the values of the <input> items to the value of the inital store items
  cancel() {
    this.customerAttributeItems$.forEach((customerAttributeItem$) => {
      const itemOrigin = this.customerAttribute.extra.items.find(
        (customerAttributeItem) => customerAttributeItem.id === customerAttributeItem$.dataset.cattrItemId,
      );

      if (itemOrigin) {
        customerAttributeItem$.checked = itemOrigin.granted!; // eslint-disable-line no-param-reassign
      }
    });
  }

  // Set the values of the store items to the values of the <input> items
  save() {
    let thisCustomerAttributeChanged = false;

    this.customerAttributeItems$.forEach((customerAttributeItem$) => {
      const item = this.customerAttribute.extra.items.find(
        (customerAttributeItem) => customerAttributeItem.id === customerAttributeItem$.dataset.cattrItemId,
      );
      if (item && item.granted !== customerAttributeItem$.checked) {
        item.granted = !!customerAttributeItem$.checked;
        thisCustomerAttributeChanged = true;
      }
    });

    if (thisCustomerAttributeChanged) {
      this.saveCustomerAttributeToStore(this.customerAttribute);
    }
  }

  get customerAttributeItemIsGrantedOrRevoked() {
    if (!this.customerAttribute) return false;
    return this.customerAttribute.standard.items.some((item) => typeof item.granted !== 'undefined')
      || this.customerAttribute.extra.items.some((item) => typeof item.granted !== 'undefined');
  }

  get itemsDisplayStyle(): string {
    /*
    Mode       Ind  Page      hasGrantedProp  ->   cattr mode  cattr symbol
    ''         -1   register  false           ->   edit        bulletpoint
    multiedit   0   edit      false | true    ->   edit        checkbox
    multiedit  -1   view      true            ->   view        icon
    multiedit  -1   view      false           ->   bulletpoint bulletpoint
     */
    if (this.userIsInRegisterFlow) {
      return 'bulletpoint';
    }

    if (this.userIsInEditMode && !this.customerAttributeItemIsGrantedOrRevoked) {
      return 'bulletpoint';
    }

    if (this.userIsInEditMode) {
      return 'checkbox';
    }

    if (this.customerAttributeItemIsGrantedOrRevoked) {
      return 'icon';
    }

    return 'bulletpoint';
  }

  get mainComponentNode() {
    return (this.getRootNode() as ShadowRoot).host;
  }

  get renderedPortalsCacheEvents() {
    return ['action/CHANGE', 'action/CANCEL'];
  }

  get modalId() {
    return `tef-coms-customer-attribute-${this.id}`;
  }
}
